import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import GooglePlacePriceLevel from 'tripPlanner/components/GooglePlacePriceLevel'
import { getAccommodationUnavailableLabel } from 'tripPlanner/utils/accommodationPricingDisplay'
import type { TripListPrice } from './tripListTypes'
import { getGooglePlacesDollarPriceLevel } from './tripListPriceLevels'
import { getValidCartItemMemberPrice } from '../../utils/cartItemPriceSelection'

const SaleUnitCaption = styled(Caption)`
  display: inline-block;
  font-weight: ${props => props.theme.font.primary.weight.regular};
  white-space: normal;
  overflow-wrap: anywhere;
`

const LuxPlusAmount = styled(BodyText)`
  color: ${props => props.theme.palette.product.luxPlus.foreground};

  ${SaleUnitCaption} {
    color: inherit;
    font-weight: inherit;
  }
`

const StandardAmount = styled(BodyText)`
  overflow-wrap: anywhere;

  &.is-muted {
    white-space: nowrap;
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    text-decoration: line-through;
    text-decoration-thickness: ${rem(1)};
  }
`

const CurrencyAmount = styled.span`
  white-space: nowrap;
`

const UnavailableLabel = styled(BodyText)`
  overflow-wrap: anywhere;
`

interface Props {
  price: TripListPrice
  luxPlusEnabled: boolean
  compact?: boolean
}

function TripListRowPrice({ price, luxPlusEnabled, compact = false }: Props) {
  const intl = useIntl()

  if (price.price === undefined || !price.currency) {
    // An offer that told us WHY it has no price gets to say so. Falling through to the
    // blank cell below would render a hotel that needs dates identically to a place that
    // simply has no price concept, which is what made a missing rate look like a bug.
    if (price.unavailableReason) {
      // Sits where the amount would, at the amount's own size and alignment — the mobile
      // card right-aligns its price column, the desktop table left-aligns it.
      return (
        <Group direction="horizontal" horizontalAlign={compact ? 'end' : 'start'} verticalAlign="center">
          <UnavailableLabel variant="medium" weight="bold" colour="light-foreground-alt">
            {getAccommodationUnavailableLabel(price.unavailableReason, intl)}
          </UnavailableLabel>
        </Group>
      )
    }

    const priceLevel = getGooglePlacesDollarPriceLevel(price.priceLevel)
    return priceLevel ? <GooglePlacePriceLevel priceLevel={priceLevel} /> : null
  }

  const memberPrice = luxPlusEnabled ? getValidCartItemMemberPrice(price) : undefined
  const showFromLabel = price.displayType !== 'total'

  const fromLabel = intl.formatMessage({
    id: '2VC6F87d4jwCnSMVMP2i',
    defaultMessage: 'From',
    description: 'Price prefix shown above list row prices in the trip planner list view',
  })

  const saleUnit = showFromLabel && price.saleUnit
    ? intl.formatMessage(
        {
          id: 'fcmSbY6UVkeqwZUWFxn3',
          defaultMessage: '/{saleUnit}',
          description: 'Sale unit suffix shown after a formatted price in the trip planner list row',
        },
        { saleUnit: price.saleUnit },
      )
    : undefined

  if (memberPrice !== undefined) {
    return (
      <Group direction={compact ? 'horizontal' : 'vertical'} gap={compact ? 4 : 2} horizontalAlign={compact ? 'end' : 'start'} verticalAlign="center">
        <LuxPlusAmount variant="medium" weight="bold">
          <CurrencyAmount>
            <FormatCurrency value={memberPrice} currency={price.currency} format="roundedDollar" />
          </CurrencyAmount>
          {saleUnit && <SaleUnitCaption as="span" variant="large">{saleUnit}</SaleUnitCaption>}
        </LuxPlusAmount>
        <StandardAmount variant="small" className="is-muted">
          <FormatCurrency value={price.price} currency={price.currency} format="roundedDollar" />
        </StandardAmount>
      </Group>
    )
  }

  if (compact) {
    return (
      <Group direction="horizontal" gap={4} horizontalAlign="end" verticalAlign="center">
        {showFromLabel && (
          <Caption variant="large" colour="light-foreground-alt">
            {fromLabel}
          </Caption>
        )}
        <StandardAmount variant="medium" weight="bold">
          <CurrencyAmount>
            <FormatCurrency value={price.price} currency={price.currency} format="roundedDollar" />
          </CurrencyAmount>
          {saleUnit && <SaleUnitCaption as="span" variant="large" colour="light-foreground-alt">{saleUnit}</SaleUnitCaption>}
        </StandardAmount>
      </Group>
    )
  }

  // The desktop table's price cell: a 12/16 "from" line stacked straight onto
  // the 14/20 amount, which is what keeps the row inside its 52px band.
  return (
    <Group direction="vertical" gap={0} horizontalAlign="start">
      {showFromLabel && (
        <Caption variant="medium" colour="light-foreground-alt">
          {fromLabel}
        </Caption>
      )}
      <StandardAmount variant="medium" weight="bold">
        <CurrencyAmount>
          <FormatCurrency value={price.price} currency={price.currency} format="roundedDollar" />
        </CurrencyAmount>
        {saleUnit && <SaleUnitCaption as="span" variant="medium" colour="light-foreground-alt">{saleUnit}</SaleUnitCaption>}
      </StandardAmount>
    </Group>
  )
}

export default TripListRowPrice
